import { Header } from "@/components/Header";
import { useSEO } from "@/hooks/use-seo";
import { Footer } from "@/components/Footer";
import { Shield, Database, Eye, Users, Globe, Clock, Lock, Mail, ChevronRight } from "lucide-react";

const SECTIONS = [
  {
    id: "co-sbíráme",
    icon: Database,
    title: "Jaká data sbíráme",
    content: [
      {
        subtitle: "Data, která poskytujete dobrovolně",
        text: "Pokud nás kontaktujete prostřednictvím kontaktního formuláře nebo e-mailu, shromažďujeme vaše jméno a e-mailovou adresu, abychom mohli odpovědět na váš dotaz. Tato data jsou použita výhradně za tímto účelem.",
      },
      {
        subtitle: "Data shromažďovaná automaticky",
        text: "Při návštěvě webu automaticky zaznamenáváme technická data: IP adresu, typ prohlížeče, operační systém, referenční URL, navštívené stránky a časové razítko návštěvy. Tato data jsou anonymizována a slouží výhradně pro analytické účely.",
      },
      {
        subtitle: "Soubory cookie",
        text: "Používáme soubory cookie pro zajištění funkčnosti webu a analytiku návštěvnosti. Více informací najdete v našich Zásadách používání souborů cookie.",
      },
    ],
  },
  {
    id: "proc-sbíráme",
    icon: Eye,
    title: "Proč data sbíráme",
    content: [
      {
        subtitle: "Provoz webu",
        text: "Technická data jsou nezbytná pro správné fungování webu, zajištění bezpečnosti a ochranu před kybernetickými útoky.",
      },
      {
        subtitle: "Zlepšení uživatelského zážitku",
        text: "Analytická data nám pomáhají pochopit, jak návštěvníci web používají, a umožňují nám neustále zlepšovat obsah a strukturu webu.",
      },
      {
        subtitle: "Odpověď na dotazy",
        text: "Kontaktní data (jméno, e-mail) jsou zpracovávána výhradně za účelem odpovědi na váš dotaz nebo zpětnou vazbu.",
      },
      {
        subtitle: "Affiliate sledování",
        text: "Pokud kliknete na odkaz na partnerské kasino, může být ve vašem zařízení uložen affiliate cookie. Tento cookie slouží výhradně pro účely připsání provize a neobsahuje žádné osobní identifikační údaje.",
      },
    ],
  },
  {
    id: "treti-strany",
    icon: Globe,
    title: "Třetí strany a zpracovatelé",
    content: [
      {
        subtitle: "Analytické služby",
        text: "Používáme Google Analytics pro anonymizovanou analýzu návštěvnosti. Google zpracovává data v souladu se svou vlastní politikou ochrany osobních údajů. Data jsou anonymizována před odesláním do Analytics.",
      },
      {
        subtitle: "Partnerská kasina",
        text: "Partnerská kasina mohou prostřednictvím affiliate cookies sledovat, zda jste k nim přišli z našeho webu. Samotná kasina zpracovávají vaše osobní údaje (při registraci) podle svých vlastních zásad ochrany osobních údajů.",
      },
      {
        subtitle: "Hosting a infrastruktura",
        text: "Naše webové stránky jsou hostovány na serverech třetích stran v souladu s legislativou EU a GDPR. Tito poskytovatelé vystupují jako zpracovatelé dat a jsou vázáni smlouvou o zpracování dat.",
      },
    ],
  },
  {
    id: "uchovani",
    icon: Clock,
    title: "Uchovávání dat",
    content: [
      {
        subtitle: "Kontaktní komunikace",
        text: "E-mailová komunikace je uchovávána po dobu nezbytně nutnou pro vyřízení vašeho dotazu, obvykle do 12 měsíců.",
      },
      {
        subtitle: "Analytická data",
        text: "Anonymizovaná analytická data jsou uchovávána po dobu 26 měsíců v souladu s nastavením Google Analytics.",
      },
      {
        subtitle: "Soubory cookie",
        text: "Doby platnosti cookies jsou uvedeny v Zásadách používání souborů cookie. Session cookies jsou smazány po zavření prohlížeče.",
      },
    ],
  },
  {
    id: "prava",
    icon: Lock,
    title: "Vaše práva (GDPR)",
    content: [
      {
        subtitle: "Právo na přístup",
        text: "Máte právo požádat o potvrzení, zda zpracováváme vaše osobní údaje, a pokud ano, o přístup k těmto údajům.",
      },
      {
        subtitle: "Právo na opravu",
        text: "Máte právo požádat o opravu nepřesných osobních údajů, které o vás uchováváme.",
      },
      {
        subtitle: "Právo na výmaz",
        text: "Za určitých okolností máte právo požádat o výmaz svých osobních údajů (tzv. právo být zapomenut).",
      },
      {
        subtitle: "Právo na přenositelnost",
        text: "Máte právo obdržet osobní údaje, které jste nám poskytli, ve strukturovaném, strojově čitelném formátu.",
      },
      {
        subtitle: "Právo vznést námitku",
        text: "Máte právo vznést námitku proti zpracování osobních údajů pro účely přímého marketingu.",
      },
    ],
  },
];

export default function ZasadyOchranyPage() {
  useSEO({ title: "Ochrana Osobních Údajů – GDPR zásady | HrajBonus", description: "Zásady ochrany osobních údajů HrajBonus v souladu s GDPR. Jaká data zpracováváme, jak je chráníme a jaká máte práva jako uživatel.",
  url: "https://hrajbonus.com/zasady-ochrany-osobnich-udaju", });

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f8fc]">
      <Header />

      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 25% 50%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 sm:py-20 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-semibold uppercase tracking-widest">Soukromí</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5 max-w-2xl">
            Zásady ochrany osobních údajů
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl">
            Vaše soukromí je pro nás prioritou. Tato stránka transparentně vysvětluje, jaké osobní údaje sbíráme, proč je sbíráme, jak je používáme a jaká máte práva.
          </p>
          <p className="text-white/40 text-sm mt-4">Naposledy aktualizováno: leden 2026 · Platné pro hrajbonus.com</p>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-20 flex-1">
        <div className="grid lg:grid-cols-4 gap-10">

          <aside className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-5">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-4">Obsah</h3>
                <nav className="space-y-1">
                  {SECTIONS.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-gray-600 hover:text-[#003da5] py-1.5 px-2 rounded-lg hover:bg-[#f3f6fb] transition-colors"
                    >
                      {s.title}
                    </a>
                  ))}
                  <a href="#kontakt" className="block text-sm text-gray-600 hover:text-[#003da5] py-1.5 px-2 rounded-lg hover:bg-[#f3f6fb] transition-colors">
                    Kontakt pro GDPR
                  </a>
                </nav>
                <div className="mt-5 pt-5 border-t border-[#dde4ee]">
                  <a
                    href="/zasady-cookies"
                    className="flex items-center gap-2 text-xs text-[#003da5] font-semibold hover:underline"
                  >
                    <ChevronRight className="w-3.5 h-3.5" /> Zásady cookies
                  </a>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3 space-y-10">
            <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6">
              <p className="text-gray-600 leading-relaxed">
                Společnost hrajbonus.com provozuje webové stránky na adrese <strong>hrajbonus.com</strong>. Tato stránka popisuje naše postupy shromažďování, používání a sdílení informací při návštěvě webu nebo kontaktování nás. Zpracování osobních údajů provádíme v souladu s nařízením EU 2016/679 (GDPR) a platnými českými právními předpisy.
              </p>
            </div>

            {SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.id} id={section.id} className="scroll-mt-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                      <Icon className="w-4.5 h-4.5 text-white" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900">{section.title}</h2>
                  </div>
                  <div className="space-y-4">
                    {section.content.map((item) => (
                      <div key={item.subtitle} className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">{item.subtitle}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            <section id="kontakt" className="scroll-mt-8">
              <div className="bg-[#f3f6fb] rounded-2xl border border-[#dde4ee] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                    <Mail className="w-4.5 h-4.5 text-white" />
                  </div>
                  <h2 className="text-2xl font-black text-gray-900">Kontakt pro GDPR</h2>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Chcete-li uplatnit svá práva dle GDPR, máte dotaz k naší politice ochrany osobních údajů nebo chcete podat stížnost, kontaktujte nás na:
                </p>
                <a
                  href="mailto:info@aroundcasinos.com"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}
                  data-testid="link-gdpr-email"
                >
                  <Mail className="w-4 h-4" /> info@aroundcasinos.com
                </a>
                <p className="text-gray-400 text-xs mt-4">
                  Dozorovým orgánem pro ochranu osobních údajů v ČR je{" "}
                  <a href="https://www.uoou.gov.cz/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline" data-testid="link-uoou">Úřad pro ochranu osobních údajů (ÚOOÚ)</a>.
                  Zpracování probíhá v souladu s{" "}
                  <a href="https://eur-lex.europa.eu/legal-content/CS/TXT/?uri=CELEX:32016R0679" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline" data-testid="link-gdpr-regulation">nařízením EU 2016/679 (GDPR)</a>.
                </p>
              </div>
            </section>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
